import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FileText, Check, Pen, Users, Clock, Shield, ChevronRight,
  Loader2, AlertCircle, Sparkles, Plus, Mail, Heart, Star,
  ArrowRight, X
} from 'lucide-react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { apiClient } from '../api/client';
import { useAuth } from '../contexts/AuthContext';

// ====== INTERACTIVE CHARTER EXPERIENCE ======
// "What if instead of handing over a document, you handed over a moment?"

const CONFETTI_COLORS = ['#8B5CF6', '#EC4899', '#F59E0B', '#10B981', '#3B82F6', '#EF4444'];

const Confetti = ({ active }) => {
  const canvasRef = useRef(null);
  useEffect(() => {
    if (!active || !canvasRef.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
    const particles = [];
    for (let i = 0; i < 150; i++) {
      particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * -canvas.height,
        w: Math.random() * 10 + 5,
        h: Math.random() * 6 + 3,
        color: CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)],
        speed: Math.random() * 3 + 2,
        angle: Math.random() * Math.PI * 2,
        spin: (Math.random() - 0.5) * 0.2,
        drift: (Math.random() - 0.5) * 2
      });
    }
    let animId;
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particles.forEach(p => {
        p.y += p.speed;
        p.x += p.drift;
        p.angle += p.spin;
        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate(p.angle);
        ctx.fillStyle = p.color;
        ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
        ctx.restore();
        if (p.y > canvas.height + 20) {
          p.y = -20;
          p.x = Math.random() * canvas.width;
        }
      });
      animId = requestAnimationFrame(animate);
    };
    animate();
    return () => cancelAnimationFrame(animId);
  }, [active]);
  if (!active) return null;
  return <canvas ref={canvasRef} className="fixed inset-0 z-50 pointer-events-none" />;
};

// ====== STEP 1: THE ENVELOPE ======
const EnvelopeStep = ({ teenName, onOpen }) => (
  <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 via-purple-950 to-gray-900 p-4">
    <div className="text-center" data-testid="charter-envelope">
      <p className="text-purple-300 text-sm tracking-[0.3em] uppercase mb-6 animate-pulse">
        Something special is waiting
      </p>
      <div
        onClick={onOpen}
        className="relative w-72 h-44 mx-auto cursor-pointer group transition-transform duration-500 hover:scale-105"
      >
        {/* Envelope body */}
        <div className="absolute inset-0 bg-gradient-to-br from-purple-600 to-pink-500 rounded-2xl shadow-2xl shadow-purple-500/30">
          {/* Seal */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-16 h-16 bg-gradient-to-br from-yellow-400 to-amber-500 rounded-full flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
            <Heart size={24} className="text-white" />
          </div>
          {/* Flap */}
          <div className="absolute -top-1 left-0 right-0 h-24 bg-gradient-to-b from-purple-700 to-purple-600 rounded-t-2xl" style={{ clipPath: 'polygon(0 0, 50% 100%, 100% 0)' }} />
        </div>
      </div>
      <p className="text-white text-xl font-bold mt-8">
        {teenName ? `For you, ${teenName}` : 'You have something special'}
      </p>
      <p className="text-purple-300 text-sm mt-2">Tap to open</p>
    </div>
  </div>
);

// ====== STEP 2: THE SCROLL (Content Chapters) ======
const ScrollStep = ({ parentName, teenName, onAcceptReady }) => (
  <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-950 to-gray-900 text-white">
    {/* Chapter 1: Personal Message */}
    <section className="min-h-screen flex items-center justify-center p-6">
      <div className="max-w-lg text-center">
        <Heart size={40} className="mx-auto text-pink-400 mb-6" />
        <h2 className="text-3xl font-bold mb-6 bg-gradient-to-r from-purple-300 to-pink-300 bg-clip-text text-transparent">
          A Personal Note
        </h2>
        <div className="text-left space-y-4 text-purple-100 leading-relaxed">
          <p>Dear {teenName},</p>
          <p>
            I know growing up today is different from when I was your age. You&apos;re navigating a world that
            moves faster, connects wider, and sometimes feels like it demands more. I see you working
            through it all, and I&apos;m proud of who you&apos;re becoming.
          </p>
          <p>
            I found something called <span className="text-purple-300 font-semibold">HiBFF</span> — it stands for
            <em className="text-pink-300"> &quot;Hi, Brave Friends Forever.&quot;</em> It&apos;s not another app trying to waste your time.
            It&apos;s a training ground for the real world, and I think you&apos;ll genuinely like it.
          </p>
          <p className="text-purple-300">This card explains what it is, why I chose it, and what I&apos;m promising you.</p>
          <p className="mt-6 text-purple-200 italic">Keep scrolling — I&apos;ll keep it short.</p>
          <p className="text-right text-purple-300 mt-4">With love, {parentName}</p>
        </div>
        <ChevronRight size={28} className="mx-auto mt-8 text-purple-400 animate-bounce rotate-90" />
      </div>
    </section>

    {/* Chapter 2: What is HiBFF */}
    <section className="min-h-screen flex items-center justify-center p-6">
      <div className="max-w-lg">
        <h2 className="text-3xl font-bold mb-8 text-center bg-gradient-to-r from-blue-300 to-purple-300 bg-clip-text text-transparent">
          What is HiBFF?
        </h2>
        <p className="text-purple-200 text-center mb-8">Your Training Ground for the Real World</p>

        <div className="space-y-4">
          {[
            { title: 'AI Companions (AvAs)', desc: 'Your own AI friends who get to know you, never judge you, and are always there to talk. They remember your conversations and grow with you.', color: 'from-purple-500 to-violet-600' },
            { title: 'Real-World Skills', desc: 'Practice conversations, build confidence, and learn life skills — from making friends to handling tricky social situations.', color: 'from-pink-500 to-rose-600' },
            { title: 'Earn Badges', desc: '20 real-world achievement badges across 4 levels. Complete challenges, earn rewards, and build a portfolio of life skills.', color: 'from-amber-500 to-orange-600' },
            { title: 'Your Privacy First', desc: 'Your conversations are yours. I can see your progress and mood scores, but never what you talk about.', color: 'from-green-500 to-emerald-600' }
          ].map((card, i) => (
            <div key={card.title} className={`p-4 rounded-xl bg-gradient-to-r ${card.color} bg-opacity-20`} style={{ animationDelay: `${i * 0.15}s` }}>
              <h3 className="font-bold text-white mb-1">{card.title}</h3>
              <p className="text-sm text-white/80">{card.desc}</p>
            </div>
          ))}
        </div>
        <ChevronRight size={28} className="mx-auto mt-8 text-purple-400 animate-bounce rotate-90" />
      </div>
    </section>

    {/* Chapter 3: Four Promises */}
    <section className="min-h-screen flex items-center justify-center p-6">
      <div className="max-w-lg">
        <h2 className="text-3xl font-bold mb-4 text-center bg-gradient-to-r from-amber-300 to-pink-300 bg-clip-text text-transparent">
          What I&apos;m Promising You
        </h2>
        <p className="text-purple-300 text-center mb-8 text-sm">From {parentName}, with love</p>

        <div className="space-y-6">
          {[
            { num: '1', title: 'I Trust You', desc: "This isn't about monitoring you. Your AvA conversations are private. I'll only see progress scores and achievements — never your words.", icon: Shield },
            { num: '2', title: "It's Yours to Explore", desc: "I'm not going to hover. Use HiBFF your way — talk to your AvA about anything. That's what it's there for.", icon: Star },
            { num: '3', title: 'I\'ll Be Here', desc: "If something ever feels too big, there are real humans ready to help — and I'm always a conversation away too.", icon: Heart },
            { num: '4', title: 'No Strings', desc: "If after a few weeks it's not your thing, just say so. No guilt, no drama. But give it a real try first.", icon: Sparkles }
          ].map((promise) => (
            <div key={promise.num} className="flex gap-4 items-start">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500/30 to-pink-500/30 flex items-center justify-center flex-shrink-0 border border-purple-500/30">
                <promise.icon size={22} className="text-purple-300" />
              </div>
              <div>
                <h3 className="font-bold text-white text-lg">{promise.title}</h3>
                <p className="text-purple-200 text-sm mt-1 leading-relaxed">{promise.desc}</p>
              </div>
            </div>
          ))}
        </div>
        <ChevronRight size={28} className="mx-auto mt-8 text-purple-400 animate-bounce rotate-90" />
      </div>
    </section>

    {/* Chapter 4: The Acceptance */}
    <section className="min-h-screen flex items-center justify-center p-6">
      <div className="max-w-lg text-center">
        <Sparkles size={40} className="mx-auto text-amber-400 mb-6" />
        <h2 className="text-3xl font-bold mb-4 bg-gradient-to-r from-purple-300 to-pink-300 bg-clip-text text-transparent">
          Why This Actually Matters
        </h2>
        <p className="text-purple-200 leading-relaxed mb-8">
          The world doesn&apos;t come with instructions. School teaches maths and science but not how
          to handle rejection, make a real friend, speak up when it matters, or deal with the voice
          in your head that says &quot;you&apos;re not enough.&quot;
        </p>
        <p className="text-purple-200 leading-relaxed mb-8">
          <span className="text-white font-semibold">HiBFF is practice for the real world.</span> Your AvAs are there
          to listen, challenge you gently, and help you build the skills that actually matter in life.
        </p>
        <p className="text-purple-300 text-sm italic mb-10">
          You deserve tools that understand you. This is one of them.
        </p>
        <button
          onClick={onAcceptReady}
          data-testid="charter-ready-btn"
          className="px-8 py-4 bg-gradient-to-r from-purple-600 to-pink-500 text-white font-bold text-lg rounded-2xl hover:shadow-2xl hover:shadow-purple-500/30 transition-all hover:scale-105 inline-flex items-center gap-3"
        >
          I&apos;m Ready <ArrowRight size={22} />
        </button>
      </div>
    </section>
  </div>
);

// ====== STEP 3: ACCEPTANCE ======
const AcceptStep = ({ teenName, onAccept, signing }) => {
  const [name, setName] = useState(teenName || '');

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 via-purple-950 to-gray-900 p-4">
      <div className="max-w-md w-full text-center" data-testid="charter-accept-step">
        <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-purple-500 to-pink-500 rounded-2xl flex items-center justify-center">
          <Pen size={32} className="text-white" />
        </div>
        <h2 className="text-2xl font-bold text-white mb-2">Make It Official</h2>
        <p className="text-purple-300 mb-8">Type your name below to accept the charter</p>

        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Your name"
          data-testid="charter-accept-name"
          className="w-full px-6 py-4 text-2xl text-center font-bold bg-white/10 border-2 border-purple-500/50 rounded-2xl text-white placeholder-purple-400 focus:border-pink-500 focus:ring-2 focus:ring-pink-500/30 outline-none mb-6"
          autoFocus
        />

        <button
          onClick={() => name.trim() && onAccept(name.trim())}
          disabled={!name.trim() || signing}
          data-testid="charter-accept-btn"
          className="w-full py-4 bg-gradient-to-r from-purple-600 to-pink-500 text-white font-bold text-lg rounded-2xl hover:shadow-2xl hover:shadow-purple-500/30 transition-all disabled:opacity-50 flex items-center justify-center gap-3"
        >
          {signing ? <Loader2 size={24} className="animate-spin" /> : (
            <>I Accept — Let&apos;s Go <ArrowRight size={22} /></>
          )}
        </button>
      </div>
    </div>
  );
};

// ====== STEP 4: CELEBRATION ======
const CelebrationStep = ({ teenName, parentName, signedAt, onContinue }) => (
  <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 via-purple-950 to-gray-900 p-4 relative">
    <Confetti active={true} />
    <div className="max-w-md w-full text-center relative z-10" data-testid="charter-celebration">
      <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-green-400 to-emerald-500 rounded-full flex items-center justify-center animate-bounce">
        <Check size={40} className="text-white" />
      </div>
      <h1 className="text-4xl font-bold text-white mb-4">
        Welcome, {teenName}!
      </h1>
      <p className="text-purple-200 text-lg mb-8">
        Your HiBFF journey starts now
      </p>

      {/* Certificate moment */}
      <div className="p-6 bg-white/10 backdrop-blur-lg rounded-2xl border border-purple-500/30 mb-8">
        <p className="text-purple-300 text-xs uppercase tracking-widest mb-3">HiBFF Charter Certificate</p>
        <p className="text-white font-bold text-xl mb-1">{teenName}</p>
        <p className="text-purple-300 text-sm mb-3">Accepted by {parentName}</p>
        <div className="w-16 h-0.5 bg-gradient-to-r from-purple-500 to-pink-500 mx-auto mb-3" />
        <p className="text-purple-400 text-xs">
          {signedAt ? new Date(signedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) : new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
        </p>
      </div>

      <button
        onClick={onContinue}
        data-testid="charter-continue-btn"
        className="px-8 py-4 bg-gradient-to-r from-green-500 to-emerald-500 text-white font-bold text-lg rounded-2xl hover:shadow-2xl hover:shadow-green-500/30 transition-all hover:scale-105 inline-flex items-center gap-3"
      >
        Start Exploring <ArrowRight size={22} />
      </button>
    </div>
  </div>
);

// The canonical HiBFF Commitment Charter — the same four unequivocal parent
// promises referenced on the website (Parent Pack). Rendered for every
// charter regardless of what an older DB record stored.
const PARENT_PROMISES = [
  { title: 'Complete Privacy', text: 'Your practice sessions are yours alone. I will see progress and achievements — never a word of what you talk about.' },
  { title: 'Unconditional Support', text: 'However you use HiBFF, I am behind you. No judgement, no hovering, no interrogations.' },
  { title: 'Full Ownership of Your Rewards', text: 'Every Heart, badge and collectible you earn belongs to you. I will never take them away.' },
  { title: 'Consistency, Not Grades', text: 'I care that you keep showing up — not about scores. Practice is the win.' },
];

const TEEN_RESPONSE = [
  'I\u2019ll give HiBFF a genuine try.',
  'I\u2019ll speak up if something doesn\u2019t feel right.',
  'I sign when I\u2019m ready — not before.',
];

const WHAT_IT_MEANS = [
  { who: 'For you', text: 'What you say to your AvAs stays private. Your parent or guardian has signed a promise never to read your conversations.' },
  { who: 'For your parent/guardian', text: 'They see only your progress and achievements — and they\u2019ve promised to support you, not police you.' },
  { who: 'For both of you', text: 'It\u2019s a two-way deal: they signed first, you sign when you\u2019re ready. Nothing happens behind your back.' },
];

// ====== MAIN CHARTER PAGE ======
const CharterPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [charter, setCharter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [signing, setSigning] = useState(false);
  const [error, setError] = useState(null);

  // Interactive experience state
  const [experienceStep, setExperienceStep] = useState(null); // 'envelope' | 'scroll' | 'accept' | 'celebration'

  // Create form state
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createData, setCreateData] = useState({
    teen_email: '',
    teen_name: '',
    tech_free_zones: ['Dinner Table', 'Bedroom after 9pm']
  });
  const [newZone, setNewZone] = useState('');
  const [showSignModal, setShowSignModal] = useState(false);
  const [signatureMark, setSignatureMark] = useState('');

  useEffect(() => {
    fetchCharter();
  }, []);

  const fetchCharter = async () => {
    setLoading(true);
    try {
      const data = await apiClient.request('/api/family/charter');
      setCharter(data);
    } catch (err) {
      setCharter(null);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCharter = async () => {
    if (!createData.teen_email.trim() || !createData.teen_name.trim()) {
      setError('Please fill in all required fields');
      return;
    }
    setCreating(true);
    setError(null);
    try {
      await apiClient.request('/api/family/charter/create', {
        method: 'POST',
        body: JSON.stringify({
          teen_email: createData.teen_email.trim(),
          teen_name: createData.teen_name.trim(),
          tech_free_zones: createData.tech_free_zones
        })
      });
      setShowCreateForm(false);
      await fetchCharter();
    } catch (err) {
      setError(err.message || 'Failed to create charter');
    } finally {
      setCreating(false);
    }
  };

  const handleSign = async () => {
    if (!signatureMark.trim()) { setError('Please enter your initials'); return; }
    setSigning(true);
    setError(null);
    try {
      await apiClient.request(`/api/family/charter/${charter.charter_id}/sign`, {
        method: 'POST',
        body: JSON.stringify({ signature_mark: signatureMark.trim() })
      });
      await fetchCharter();
      setShowSignModal(false);
      setSignatureMark('');
    } catch (err) {
      setError(err.message || 'Failed to sign');
    } finally { setSigning(false); }
  };

  // Interactive experience acceptance
  const handleInteractiveAccept = async (name) => {
    setSigning(true);
    try {
      await apiClient.request(`/api/family/charter/${charter.charter_id}/sign`, {
        method: 'POST',
        body: JSON.stringify({ signature_mark: name.substring(0, 5).toUpperCase() })
      });
      await fetchCharter();
      setExperienceStep('celebration');
    } catch (err) {
      setError(err.message || 'Failed to accept charter');
    } finally { setSigning(false); }
  };

  const addZone = () => {
    if (newZone.trim() && !createData.tech_free_zones.includes(newZone.trim())) {
      setCreateData({ ...createData, tech_free_zones: [...createData.tech_free_zones, newZone.trim()] });
      setNewZone('');
    }
  };
  const removeZone = (zone) => {
    setCreateData({ ...createData, tech_free_zones: createData.tech_free_zones.filter(z => z !== zone) });
  };

  const isParent = charter?.parent_id === user?.user_id;
  const isTeen = charter?.teen_id === user?.user_id;
  const canSign = (isParent && !charter?.parent_signed) || (isTeen && !charter?.teen_signed && charter?.parent_signed);

  // ====== INTERACTIVE EXPERIENCE MODE ======
  if (experienceStep === 'envelope') {
    return <EnvelopeStep teenName={charter?.teen_name} onOpen={() => setExperienceStep('scroll')} />;
  }
  if (experienceStep === 'scroll') {
    return <ScrollStep parentName={charter?.parent_name} teenName={charter?.teen_name} onAcceptReady={() => setExperienceStep('accept')} />;
  }
  if (experienceStep === 'accept') {
    return <AcceptStep teenName={charter?.teen_name} onAccept={handleInteractiveAccept} signing={signing} />;
  }
  if (experienceStep === 'celebration') {
    return <CelebrationStep teenName={charter?.teen_name} parentName={charter?.parent_name} signedAt={charter?.teen_signed_at} onContinue={() => { setExperienceStep(null); navigate('/dashboard'); }} />;
  }

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[60vh]" data-testid="charter-loading">
          <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
        </div>
      </DashboardLayout>
    );
  }

  // ====== NO CHARTER — CREATION FLOW ======
  if (!charter) {
    return (
      <DashboardLayout>
        <div className="max-w-3xl mx-auto p-4 sm:p-6">
          {!showCreateForm ? (
            <div className="text-center py-12" data-testid="charter-empty-state">
              <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-purple-100 to-pink-100 rounded-2xl flex items-center justify-center">
                <FileText size={40} className="text-purple-500" />
              </div>
              <h1 className="text-3xl font-bold text-gray-900 mb-4">Charter Doc</h1>
              <p className="text-gray-700 mb-2 max-w-lg mx-auto">
                The HiBFF Commitment Charter — a signed promise between parent and teen: privacy, support, ownership and consistency.
              </p>
              <p className="text-sm text-gray-500 mb-8 max-w-md mx-auto">
                Parents create the charter and sign first. Then the teen receives an interactive invitation to co-sign.
              </p>
              <button
                onClick={() => setShowCreateForm(true)}
                data-testid="create-charter-btn"
                className="px-8 py-3.5 bg-gradient-to-r from-purple-600 to-pink-500 text-white font-semibold rounded-xl hover:shadow-lg transition-all inline-flex items-center gap-2"
              >
                <Plus size={20} /> Create Charter
              </button>
            </div>
          ) : (
            <div data-testid="charter-create-form">
              <button onClick={() => setShowCreateForm(false)} className="text-sm text-gray-500 hover:text-gray-700 mb-4">&larr; Back</button>
              <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
                <div className="p-6 bg-gradient-to-r from-purple-50 to-pink-50 border-b">
                  <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
                    <FileText size={28} className="text-purple-500" />
                    Create a New Charter
                  </h2>
                  <p className="text-gray-600 mt-1">Set up a family commitment to real-world connection</p>
                </div>
                <div className="p-6 space-y-5">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">Teen&apos;s Name *</label>
                    <input type="text" value={createData.teen_name} onChange={(e) => setCreateData({ ...createData, teen_name: e.target.value })} placeholder="e.g., Alex" data-testid="charter-teen-name" className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:border-purple-500 focus:ring-2 focus:ring-purple-200 outline-none" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">Teen&apos;s Email *</label>
                    <div className="relative">
                      <Mail size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                      <input type="email" value={createData.teen_email} onChange={(e) => setCreateData({ ...createData, teen_email: e.target.value })} placeholder="teen@email.com" data-testid="charter-teen-email" className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-xl focus:border-purple-500 focus:ring-2 focus:ring-purple-200 outline-none" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">Tech-Free Zones</label>
                    <div className="flex flex-wrap gap-2 mb-3">
                      {createData.tech_free_zones.map(zone => (
                        <span key={zone} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-purple-50 text-purple-700 rounded-full text-sm">
                          {zone} <button onClick={() => removeZone(zone)} className="text-purple-400 hover:text-purple-600">&times;</button>
                        </span>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <input type="text" value={newZone} onChange={(e) => setNewZone(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addZone())} placeholder="Add a tech-free zone..." className="flex-1 px-4 py-2.5 border border-gray-300 rounded-xl focus:border-purple-500 focus:ring-2 focus:ring-purple-200 outline-none text-sm" />
                      <button onClick={addZone} className="px-4 py-2.5 bg-purple-100 text-purple-700 rounded-xl hover:bg-purple-200 text-sm font-medium">Add</button>
                    </div>
                  </div>
                  {error && <div className="p-3 bg-red-50 text-red-700 rounded-lg flex items-center gap-2 text-sm"><AlertCircle size={16} />{error}</div>}
                  <button onClick={handleCreateCharter} disabled={creating} data-testid="submit-charter-btn" className="w-full py-3.5 bg-gradient-to-r from-purple-600 to-pink-500 text-white font-semibold rounded-xl hover:shadow-lg disabled:opacity-50 flex items-center justify-center gap-2">
                    {creating ? <Loader2 size={20} className="animate-spin" /> : <><FileText size={20} /> Create Charter</>}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </DashboardLayout>
    );
  }

  // ====== HAS CHARTER — DOCUMENT VIEW ======
  return (
    <DashboardLayout>
      <div className="max-w-3xl mx-auto p-4 sm:p-6" data-testid="charter-page">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-500 rounded-2xl mb-4">
            <FileText size={32} className="text-white" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">The HiBFF Commitment Charter</h1>
          <p className="text-gray-600">Your parent or guardian signs first. You sign when you&apos;re ready.</p>
          <div className={`inline-flex items-center gap-2 mt-4 px-4 py-2 rounded-full text-sm font-semibold ${charter.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`} data-testid="charter-status">
            {charter.status === 'active' ? <><Check size={16} /> Signed &amp; Active</> : <><Clock size={16} /> Awaiting Signature</>}
          </div>
        </div>

        {/* Interactive Experience Button (for teens who haven't signed) */}
        {canSign && isTeen && (
          <div className="mb-6 p-4 bg-gradient-to-r from-purple-600 to-pink-500 rounded-2xl text-white text-center">
            <p className="font-semibold mb-2">Your parent has prepared something special for you</p>
            <button
              onClick={() => setExperienceStep('envelope')}
              data-testid="start-charter-experience-btn"
              className="px-6 py-3 bg-white text-purple-700 font-bold rounded-xl hover:shadow-lg transition-all inline-flex items-center gap-2"
            >
              <Sparkles size={18} /> Open the Invitation
            </button>
          </div>
        )}

        {/* Charter Document Card */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
          {/* Parties */}
          <div className="p-6 bg-gradient-to-r from-purple-50 to-pink-50 border-b">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-purple-500 rounded-full flex items-center justify-center text-white font-bold text-lg">{charter.parent_name?.charAt(0) || 'P'}</div>
                <div>
                  <p className="font-semibold text-gray-900">{charter.parent_name}</p>
                  <p className="text-sm text-gray-600">Parent/Guardian</p>
                </div>
              </div>
              <div className="w-10 h-10 rounded-full bg-white/80 flex items-center justify-center"><Users size={20} className="text-purple-500" /></div>
              <div className="flex items-center gap-3">
                <div className="text-right">
                  <p className="font-semibold text-gray-900">{charter.teen_name}</p>
                  <p className="text-sm text-gray-600">Teen</p>
                </div>
                <div className="w-12 h-12 bg-pink-500 rounded-full flex items-center justify-center text-white font-bold text-lg">{charter.teen_name?.charAt(0) || 'T'}</div>
              </div>
            </div>
          </div>

          {/* The Charter Document */}
          <div className="p-6 sm:p-8" data-testid="charter-document">
            <p className="text-center text-[11px] uppercase tracking-[0.25em] text-gray-400 mb-6">The HiBFF Commitment Charter</p>

            <h3 className="flex items-center gap-2 text-lg font-semibold text-purple-700 mb-1"><Shield size={20} /> Four Unequivocal Promises</h3>
            <p className="text-sm text-gray-500 mb-4">Made by {charter.parent_name || 'your parent/guardian'} to {charter.teen_name || 'you'}:</p>
            <div className="space-y-3 mb-8">
              {PARENT_PROMISES.map((p, i) => (
                <div key={p.title} className="flex items-start gap-3 p-4 bg-purple-50 rounded-xl" data-testid={`charter-promise-${i + 1}`}>
                  <span className="w-7 h-7 rounded-full bg-purple-600 text-white text-sm font-bold flex items-center justify-center flex-shrink-0 mt-0.5">{i + 1}</span>
                  <div>
                    <p className="font-semibold text-gray-900">{p.title}</p>
                    <p className="text-sm text-gray-700 mt-0.5">{p.text}</p>
                  </div>
                </div>
              ))}
            </div>

            <h3 className="flex items-center gap-2 text-lg font-semibold text-pink-700 mb-1"><Sparkles size={20} /> The Teen&apos;s Response</h3>
            <p className="text-sm text-gray-500 mb-4">Signed digitally in return — when they&apos;re ready. Not before.</p>
            <div className="space-y-2 mb-2">
              {TEEN_RESPONSE.map((t, i) => (
                <div key={i} className="flex items-start gap-3 p-3 bg-pink-50 rounded-lg">
                  <Check size={18} className="text-pink-500 mt-0.5 flex-shrink-0" />
                  <p className="text-gray-700 text-sm">{t}</p>
                </div>
              ))}
            </div>
            {charter.tech_free_zones?.length > 0 && (
              <div className="mt-6">
                <h3 className="text-base font-semibold text-gray-700 mb-3">Our Tech-Free Zones</h3>
                <div className="flex flex-wrap gap-2">
                  {charter.tech_free_zones.map((zone) => (<span key={zone} className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded-full text-sm">{zone}</span>))}
                </div>
              </div>
            )}
          </div>

          {/* Signatures */}
          <div className="p-6 bg-gray-50 border-t">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Signatures</h3>
            <div className="grid grid-cols-2 gap-6">
              <div className={`p-4 rounded-xl border-2 ${charter.parent_signed ? 'border-green-300 bg-green-50' : 'border-dashed border-gray-300'}`} data-testid="parent-signature">
                <p className="text-sm text-gray-600 mb-2">Parent/Guardian</p>
                {charter.parent_signed ? (
                  <div>
                    <p className="text-2xl font-bold italic text-purple-600">{charter.parent_signature_mark}</p>
                    <p className="text-xs text-gray-500 mt-1">Signed {new Date(charter.parent_signed_at).toLocaleDateString()}</p>
                  </div>
                ) : <p className="text-gray-400 italic">Awaiting signature</p>}
              </div>
              <div className={`p-4 rounded-xl border-2 ${charter.teen_signed ? 'border-green-300 bg-green-50' : 'border-dashed border-gray-300'}`} data-testid="teen-signature">
                <p className="text-sm text-gray-600 mb-2">Teen</p>
                {charter.teen_signed ? (
                  <div>
                    <p className="text-2xl font-bold italic text-pink-600">{charter.teen_signature_mark}</p>
                    <p className="text-xs text-gray-500 mt-1">Signed {new Date(charter.teen_signed_at).toLocaleDateString()}</p>
                  </div>
                ) : <p className="text-gray-400 italic">{charter.parent_signed ? 'Awaiting signature' : 'Parent signs first'}</p>}
              </div>
            </div>

            {canSign && isParent && (
              <button onClick={() => setShowSignModal(true)} data-testid="sign-charter-btn" className="w-full mt-6 py-3.5 bg-gradient-to-r from-purple-600 to-pink-500 text-white font-semibold rounded-xl hover:shadow-lg transition-all flex items-center justify-center gap-2">
                <Pen size={20} /> Sign Charter
              </button>
            )}

            {charter.status === 'active' && (
              <div className="mt-6 p-4 bg-green-100 rounded-xl text-center">
                <p className="text-green-800 font-semibold">Charter is active! Badge unlocked.</p>
                <button onClick={() => navigate('/badges')} className="mt-2 text-green-700 font-medium hover:underline inline-flex items-center gap-1">
                  View your badges <ChevronRight size={16} />
                </button>
              </div>
            )}
          </div>
        </div>

        {/* What this actually means */}
        <div className="mt-8 bg-white rounded-2xl shadow-lg border border-gray-200 p-6 sm:p-8" data-testid="charter-what-it-means">
          <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2"><Heart size={20} className="text-pink-500" /> What this actually means</h2>
          <div className="space-y-4">
            {WHAT_IT_MEANS.map((m) => (
              <div key={m.who} className="flex items-start gap-3">
                <Check size={18} className="text-green-500 mt-1 flex-shrink-0" />
                <p className="text-sm text-gray-700"><span className="font-semibold text-gray-900">{m.who}:</span> {m.text}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Sign Modal (for parent) */}
        {showSignModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md" data-testid="sign-modal">
              <div className="p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-2">Sign the Charter</h2>
                <p className="text-gray-600 mb-6 text-sm">Enter your initials below to commit to the charter.</p>
                <input type="text" value={signatureMark} onChange={(e) => setSignatureMark(e.target.value.toUpperCase())} maxLength={5} placeholder="e.g., JS" data-testid="signature-input" className="w-full px-4 py-3 text-2xl text-center font-bold italic border-2 border-gray-300 rounded-xl focus:border-purple-500 focus:ring-2 focus:ring-purple-200 outline-none mb-4" />
                {error && <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-lg flex items-center gap-2 text-sm"><AlertCircle size={16} />{error}</div>}
                <div className="flex gap-3">
                  <button onClick={() => setShowSignModal(false)} className="flex-1 py-3 border border-gray-300 text-gray-700 font-semibold rounded-xl hover:bg-gray-50">Cancel</button>
                  <button onClick={handleSign} disabled={signing || !signatureMark.trim()} data-testid="confirm-sign-btn" className="flex-1 py-3 bg-gradient-to-r from-purple-600 to-pink-500 text-white font-semibold rounded-xl hover:shadow-lg disabled:opacity-50 flex items-center justify-center gap-2">
                    {signing ? <Loader2 size={20} className="animate-spin" /> : <><Pen size={20} /> Sign</>}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default CharterPage;
